import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Download, Loader2, Search, ClipboardList, Eye } from 'lucide-react';
import GigCodeOfConductModal from '../../components/GigCodeOfConductModal';

export default function AdminCoCAcceptances() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [q, setQ] = useState('');
  const [versionId, setVersionId] = useState('all');
  const [status, setStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [previewVersion, setPreviewVersion] = useState(null);

  const buildParams = useCallback(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (versionId !== 'all') p.version_id = versionId;
    if (status !== 'all') p.status = status;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [q, versionId, status, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/code-of-conduct/acceptances', { params: { ...buildParams(), limit: 500 } });
      setRows(r.data?.acceptances || []);
    } catch (e) {
      toast.error('Failed to load acceptances');
    } finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/admin/code-of-conduct/versions'); setVersions(r.data || []); } catch (e) { /* noop */ }
    })();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    try {
      const r = await api.get('/admin/code-of-conduct/acceptances/export', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `coc_acceptances_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const openVersion = async (vid) => {
    try { const r = await api.get(`/code-of-conduct/versions/${vid}`); setPreviewVersion(r.data); }
    catch (e) { toast.error('Could not load that version'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-coc-acceptances-page">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate('/admin/code-of-conduct')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="acceptances-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Code of Conduct
        </button>

        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Code of Conduct Acceptances</h1>
              <p className="text-sm text-slate-500">Full compliance audit trail across the platform.</p>
            </div>
          </div>
          <Button variant="outline" onClick={exportCsv} data-testid="acceptances-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>

        <Card className="mb-4">
          <CardContent className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Search talent / event / venue</label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" placeholder="Name, event, venue…" data-testid="acceptances-search" onKeyDown={(e) => e.key === 'Enter' && load()} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">CoC version</label>
              <Select value={versionId} onValueChange={setVersionId}>
                <SelectTrigger data-testid="acceptances-version-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All versions</SelectItem>
                  {versions.map((v) => <SelectItem key={v.id} value={v.id}>{v.version_label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="acceptances-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs font-semibold text-slate-600">From</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="acceptances-date-from" />
              </div>
              <div className="flex-1">
                <label className="text-xs font-semibold text-slate-600">To</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="acceptances-date-to" />
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-5">
              <Button className="bg-slate-800 hover:bg-slate-900" onClick={load} data-testid="acceptances-apply-filters-btn">Apply filters</Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="acceptances-table">
                <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Talent</th>
                    <th className="text-left px-3 py-2">Event / Venue</th>
                    <th className="text-left px-3 py-2">Client</th>
                    <th className="text-left px-3 py-2">CoC Version</th>
                    <th className="text-left px-3 py-2">Path</th>
                    <th className="text-left px-3 py-2">Accepted At</th>
                    <th className="text-left px-3 py-2">IP / Device</th>
                    <th className="text-left px-3 py-2">Booking / Shift</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-slate-400 py-10" data-testid="acceptances-empty">No acceptance records match your filters.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`acceptance-row-${r.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{r.talent_name || '—'}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{r.talent_id.slice(0, 8)}…</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate-800">{r.event_title || r.venue_name || '—'}</div>
                        <div className="text-[11px] text-slate-400">{r.shift_date || ''}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{r.client_name || '—'}</td>
                      <td className="px-3 py-2">
                        <button className="text-teal-700 hover:underline inline-flex items-center gap-1" onClick={() => openVersion(r.coc_version_id)} data-testid={`acceptance-view-version-${r.id}`}>
                          <Eye className="w-3.5 h-3.5" />{r.coc_version_label}
                        </button>
                      </td>
                      <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.accept_path}</Badge></td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.accepted_at ? new Date(r.accepted_at).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-500 max-w-[180px] truncate" title={r.user_agent || ''}>{r.ip_address || '—'}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-400 font-mono">
                        {r.booking_id ? `${r.booking_id.slice(0, 8)}…` : '—'}<br />{r.shift_id ? `${r.shift_id.slice(0, 8)}…` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      <GigCodeOfConductModal open={!!previewVersion} version={previewVersion} readOnly onClose={() => setPreviewVersion(null)} heading={previewVersion ? `Accepted policy · ${previewVersion.version_label}` : undefined} />
    </div>
  );
}
